import { el } from "@dhx/ts-common/dom";
import { ICol, IRendererConfig, GridEvents, IRow, IEditor } from "../../types";
import { isNumeric } from "@dhx/ts-common/core";
import { getCalculatedRowHeight, getMaxRowHeight, getTreeCellWidthOffset } from "../../helpers/data";

export class TextAreaEditor implements IEditor {
	protected _handlers: { [key: string]: (...args) => void };
	protected _cell: { row: IRow; col: ICol };
	protected _config: IRendererConfig;
	protected _editor: HTMLTextAreaElement;

	private _minHeight: number;
	private _prevHeight: number;
	private _width: number;

	constructor(row: IRow, col: ICol, config: IRendererConfig) {
		this._config = config;
		this._cell = { row, col };

		this._width = this._cell.col.$width;
		let widthOffset = 0;

		if (this._config.firstColId === this._cell.col.id && this._cell.row.hasOwnProperty("$level")) {
			widthOffset = getTreeCellWidthOffset(this._cell.row);
		}
		this._width -= widthOffset - 4;

		this._initHandlers();
	}

	endEdit(withoutSave?: boolean) {
		let value: string | number;
		if (!withoutSave) {
			value = this._editor.value;
		}
		if (this._config.events.fire(GridEvents.beforeEditEnd, [value, this._cell.row, this._cell.col])) {
			this._editor.removeEventListener("blur", this._handlers.onBlur);
			this._editor.removeEventListener("change", this._handlers.onChange);
			this._editor.removeEventListener("input", this._handlers.onInput);
			if (this._cell.col.type !== "string" && isNumeric(value)) {
				value = parseFloat(value as string);
			}
			this._cell.row = this._config.datacollection.getItem(this._cell.row.id);
			this._config.events.fire(GridEvents.afterEditEnd, [value, this._cell.row, this._cell.col]);
		} else {
			this._editor.focus();
		}
	}

	toHTML() {
		let value = this._cell.row[this._cell.col.id];
		if (this._editor) {
			value = this._editor.value;
		}
		this._config.$editable.editor = this;

		const css =
			typeof this._cell.row.height === "undefined" && !this._cell.col.htmlEnable
				? "dhx_cell-editor dhx_cell-editor__textarea"
				: "dhx_cell-editor dhx_cell-editor__textarea_constant-height";

		return el("textarea", {
			_hooks: {
				didInsert: this._handlers.didInsert,
			},
			_ref: "textarea",
			_key: "cell_editor",
			"data-dhx-id": "cell_editor",
			value,
			class: css,
			style: {
				width: this._width,
			},
		});
	}

	protected _initHandlers() {
		this._handlers = {
			onBlur: () => {
				this.endEdit();
			},
			onChange: () => {
				this.endEdit();
			},
			onInput: event => {
				if (typeof this._cell.row.height !== "undefined" || this._cell.col.htmlEnable) return;

				const currentHeight = this._getCurrentHeight(this._editor.value, {
					width: this._cell.col.$width - 2,
					maxHeight: this._config.rowHeight,
				});

				this._cell.row[this._cell.col.id] = this._editor.value;

				const totalHeight = getCalculatedRowHeight(
					getMaxRowHeight(this._cell.row, this._config.columns)
				);

				const currentRowHeight = getCalculatedRowHeight(
					getMaxRowHeight(
						{ [this._cell.col.id]: this._cell.row[this._cell.col.id] },
						this._config.columns
					)
				);

				this._minHeight = totalHeight === currentRowHeight ? this._config.rowHeight : totalHeight;

				if (currentHeight >= this._minHeight && currentHeight !== this._prevHeight) {
					if (
						!this._config.events.fire(GridEvents.beforeRowResize, [
							this._cell.row,
							event,
							currentHeight,
						])
					) {
						return;
					}
					this._config.events.fire(GridEvents.afterRowResize, [
						this._cell.row,
						event,
						currentHeight,
					]);
				}
				this._prevHeight = currentHeight;
			},
			didInsert: node => {
				this._editor = node.el;

				this._editor.focus();
				this._editor.setSelectionRange(0, this._editor.value.length);
				this._editor.addEventListener("change", this._handlers.onChange);
				this._editor.addEventListener("blur", this._handlers.onBlur);
				this._editor.addEventListener("input", this._handlers.onInput);
			},
		};
	}

	private _getCurrentHeight(
		value: string,
		config?: { width: number; maxHeight: number; padding?: number; lineHeight?: number }
	): number {
		config = {
			width: 100,
			maxHeight: 40,
			lineHeight: 20,
			...config,
		};

		const element = document.createElement("textarea");
		element.className = "dhx_cell-editor dhx_cell-editor__textarea";
		element.value = value;
		element.style.width = `${config.width}px`;
		element.style.lineHeight = `${config.lineHeight}px`;
		element.style.maxHeight = `${config.maxHeight}px`;
		element.style.boxSizing = "border-box";

		document.body.appendChild(element);
		const height = this._getElementHeight(element);
		const currentRows = element.value.split("\n").length;
		const calculateRows = Math.round(height / config.lineHeight);
		const calculateHeight = height < config.maxHeight ? config.maxHeight : height;

		document.body.removeChild(element);

		return currentRows === 1 && currentRows === calculateRows ? config.maxHeight : calculateHeight;
	}

	private _getElementHeight(element: HTMLElement | HTMLTextAreaElement): number {
		return element.scrollHeight;
	}
}
